import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { FiChevronLeft, FiChevronRight, FiX, FiAlertCircle, FiLink } from "react-icons/fi";
import api from "../../../services/api";
import {
  Backdrop, Sheet, SheetNav, SheetBody, Hero, StatStrip, Group, GroupLabel, Row, IconTile, Pill, CodeBlock,
  IconButton, LoadMore, Meter, Skeleton, tokens,
} from "../styles";
import { Avatar } from "./primitives";
import Feed from "./Feed";
import {
  IOS, MODULE_META, describe, eventMeta, moduleMeta, screenLabel, fullDateTime, fmtDuration, relativeTime, isOnline, timeLabel,
} from "../labels";

const PAGE = 40;
const NavIcon = MODULE_META.navigation.icon;

/**
 * Folha lateral com navegação em pilha (estilo UINavigationController):
 * projeto → usuário → evento → projeto ... com botão "‹ Voltar" mostrando
 * o título da página anterior.
 */
export default function DetailSheet({ stack, onPush, onPop, onClose, baseParams, lookup }) {
  const bodyRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const page = stack[stack.length - 1];
  const prev = stack[stack.length - 2];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") (stack.length > 1 ? onPop() : onClose());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stack.length, onPop, onClose]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    setScrolled(false);
  }, [stack.length, page]);

  if (!page) return null;

  return (
    <>
      <Backdrop onClick={onClose} />
      <Sheet role="dialog" aria-modal="true">
        <SheetNav $scrolled={scrolled}>
          {prev ? (
            <button className="back" onClick={onPop}>
              <FiChevronLeft /><span>{titleOf(prev)}</span>
            </button>
          ) : <span />}
          <span className="nav-title">{titleOf(page)}</span>
          <IconButton className="close" onClick={onClose} aria-label="Fechar"><FiX /></IconButton>
        </SheetNav>
        <SheetBody ref={bodyRef} onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 60)}>
          <div className="page" key={`${stack.length}-${page.kind}-${page.item.id}`}>
            {page.kind === "event" ? (
              <EventPage event={page.item} onPush={onPush} lookup={lookup} />
            ) : (
              <EntityPage kind={page.kind} item={page.item} onPush={onPush} baseParams={baseParams} lookup={lookup} />
            )}
          </div>
        </SheetBody>
      </Sheet>
    </>
  );
}

const titleOf = (p) => (p.kind === "event" ? describe(p.item) : p.item.name || "Detalhe");

// ─── Página de projeto / usuário ────────────────────────────────────────────
function EntityPage({ kind, item, onPush, baseParams, lookup }) {
  const theme = useTheme();
  const isUser = kind === "user";
  const filterKey = isUser ? "userId" : "projectId";
  const full = { ...(isUser ? lookup.users[item.id] : lookup.projects[item.id]), ...item };
  const accent = full.color || (isUser ? IOS.green : IOS.indigo);

  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const params = { ...baseParams, [filterKey]: item.id };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/activity-logs/summary", { params }),
      api.get("/activity-logs", { params: { ...params, size: PAGE, from: 0 } }),
    ]).then(([s, l]) => {
      if (!alive) return;
      setSummary(s.data.summary);
      setEvents(l.data.items || []);
      setTotal(l.data.total || 0);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, JSON.stringify(baseParams)]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const r = await api.get("/activity-logs", { params: { ...params, size: PAGE, from: events.length } });
      setEvents((p) => [...p, ...(r.data.items || [])]);
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.length, item.id]);

  const related = isUser ? summary?.byProject || [] : summary?.byUser || [];
  const relatedMax = Math.max(1, ...related.map((r) => r.count));
  const screens = summary?.byScreen || [];
  const screensMax = Math.max(1, ...screens.map((s) => s.count));
  const lastAt = events[0]?.timestamp || full.lastAt;
  const pageViews = (summary?.byType || []).find((t) => t.key === "page_view")?.count || 0;
  const actions = (summary?.byType || []).find((t) => t.key === "action")?.count || 0;

  return (
    <>
      <Hero>
        <Avatar id={item.id} name={full.name} color={full.color} size={78} square={!isUser} online={isUser && isOnline(lastAt)} />
        <h3>{full.name}</h3>
        <div className="hero-sub">
          {full.subtitle && <span>{full.subtitle}</span>}
          {full.cargo === "god" && <Pill $color={IOS.red}>god</Pill>}
          {lastAt && (
            isOnline(lastAt)
              ? <Pill $color={IOS.green}>● ativo agora</Pill>
              : <span>visto {relativeTime(lastAt)}</span>
          )}
        </div>
      </Hero>

      <StatStrip $cols={4}>
        <div><div className="v">{loading ? "–" : summary?.total || 0}</div><div className="l">eventos</div></div>
        <div><div className="v">{loading ? "–" : actions}</div><div className="l">ações</div></div>
        <div><div className="v">{loading ? "–" : pageViews}</div><div className="l">telas</div></div>
        <div>
          <div className="v" style={summary?.errors ? { color: IOS.red } : undefined}>{loading ? "–" : summary?.errors || 0}</div>
          <div className="l">erros</div>
        </div>
      </StatStrip>

      {/* Mini gráfico */}
      <Group style={{ marginTop: 14, padding: "12px 6px 4px" }}>
        {loading ? <div style={{ padding: "0 10px 10px" }}><Skeleton $h={90} $r={10} /></div> : (
          <ResponsiveContainer width="100%" height={96}>
            <AreaChart data={(summary?.byDay || []).map((d) => ({ ...d, label: d.day.slice(8, 10) + "/" + d.day.slice(5, 7) }))} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id={`sh-${item.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" hide />
              <Tooltip
                cursor={{ stroke: tokens.label3(theme), strokeDasharray: "3 3" }}
                contentStyle={{ background: tokens.solid(theme), border: `1px solid ${tokens.separator(theme)}`, borderRadius: 10, boxShadow: "0 6px 20px rgba(0,0,0,0.2)", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: tokens.label(theme) }}
                labelStyle={{ fontWeight: 600 }}
                formatter={(v) => [v, "eventos"]}
              />
              <Area type="monotone" dataKey="total" stroke={accent} strokeWidth={2} fill={`url(#sh-${item.id})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Group>

      {/* Relacionados */}
      <GroupLabel style={{ marginTop: 24 }}>
        <span>{isUser ? "Projetos" : "Usuários"}</span>
        <span>{related.length}</span>
      </GroupLabel>
      <Group>
        {loading && <Row><div className="main"><Skeleton $w="50%" $h={12} /></div></Row>}
        {!loading && related.length === 0 && <Row><div className="main"><div className="subtitle">Nenhum registro</div></div></Row>}
        {!loading && related.map((r) => {
          const meta = (isUser ? lookup.projects : lookup.users)[r.key] || {};
          return (
            <Row key={r.key} $clickable $inset="62px" onClick={() => onPush({ kind: isUser ? "project" : "user", item: { id: r.key, name: meta.name || r.name, color: meta.color, subtitle: meta.subtitle, cargo: meta.cargo } })}>
              <Avatar id={r.key} name={meta.name || r.name} color={meta.color} size={34} square={isUser} online={!isUser && isOnline(meta.lastAt)} />
              <div className="main">
                <div className="title">{meta.name || r.name}</div>
                <Meter $color={meta.color || (isUser ? IOS.indigo : IOS.green)}><div style={{ width: `${(r.count / relatedMax) * 100}%` }} /></Meter>
              </div>
              <div className="trailing">
                <span className="value">{r.count}</span>
                <FiChevronRight className="chevron" />
              </div>
            </Row>
          );
        })}
      </Group>

      {screens.length > 0 && (
        <>
          <GroupLabel style={{ marginTop: 24 }}><span>Telas mais acessadas</span><span>{screens.length}</span></GroupLabel>
          <Group>
            {screens.slice(0, 6).map((s) => (
              <Row key={s.key} $dense $inset="58px">
                <IconTile $size={28} $color={MODULE_META.navigation.color}><NavIcon /></IconTile>
                <div className="main">
                  <div className="title">{screenLabel(s.key)}</div>
                  <Meter $color={IOS.blue}><div style={{ width: `${(s.count / screensMax) * 100}%` }} /></Meter>
                </div>
                <div className="trailing"><span className="value">{s.count}</span></div>
              </Row>
            ))}
          </Group>
        </>
      )}

      <div style={{ marginTop: 26, padding: "0 4px 8px", fontWeight: 600, fontSize: "0.86em", letterSpacing: "-0.01em", opacity: 0.85 }}>
        Linha do tempo
      </div>
      <Feed
        items={events}
        loading={loading}
        showUser={!isUser}
        showProject={isUser}
        onSelect={(e) => onPush({ kind: "event", item: e })}
      />
      {!loading && events.length < total && (
        <LoadMore onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? "Carregando..." : `Mostrar mais ${Math.min(PAGE, total - events.length)}`}
        </LoadMore>
      )}
    </>
  );
}

// ─── Página de evento ───────────────────────────────────────────────────────
function EventPage({ event, onPush, lookup }) {
  const theme = useTheme();
  const [opEvents, setOpEvents] = useState([]);
  const meta = eventMeta(event);
  const Icon = event.status === "error" ? FiAlertCircle : meta.icon;
  const color = event.status === "error" ? IOS.red : meta.color;

  useEffect(() => {
    setOpEvents([]);
    if (!event.operationId) return;
    api.get(`/activity-logs/operation/${encodeURIComponent(event.operationId)}`)
      .then((r) => setOpEvents((r.data.items || []).filter((x) => x.id !== event.id)))
      .catch(() => {});
  }, [event.id, event.operationId]);

  const userMeta = lookup.users[event.userId] || {};
  const projectMeta = lookup.projects[event.projectId] || {};

  const InfoRow = ({ label, children, mono }) => (
    <Row $dense>
      <div style={{ width: 120, flexShrink: 0, fontSize: "0.86em" }}>{label}</div>
      <div className="main" style={{ textAlign: "right", fontSize: "0.86em", color: tokens.label2(theme), wordBreak: "break-word", fontFamily: mono ? "'JetBrains Mono', monospace" : undefined }}>
        {children}
      </div>
    </Row>
  );

  return (
    <>
      <Hero>
        <IconTile $size={68} $color={color}><Icon /></IconTile>
        <h3>{describe(event)}</h3>
        <div className="hero-sub">
          <span>{fullDateTime(event.timestamp)}</span>
        </div>
        <div className="hero-sub" style={{ marginTop: 8 }}>
          <Pill $color={color}>{event.status === "error" ? `Erro${event.httpStatus ? ` ${event.httpStatus}` : ""}` : "Sucesso"}</Pill>
          <Pill $color={meta.color}>{event.type === "page_view" ? "Tela" : event.type === "task" ? "Tarefa" : "Ação"}</Pill>
          {event.realUserId && <Pill $color={IOS.orange}>Simulado</Pill>}
        </div>
      </Hero>

      {event.status === "error" && event.errorMessage && (
        <Group style={{ marginTop: 16, background: `${IOS.red}12`, borderColor: `${IOS.red}40` }}>
          <Row $dense>
            <FiAlertCircle style={{ color: IOS.red, flexShrink: 0 }} />
            <div className="main" style={{ fontSize: "0.86em", color: IOS.red, whiteSpace: "normal" }}>{event.errorMessage}</div>
          </Row>
        </Group>
      )}

      <GroupLabel style={{ marginTop: 22 }}><span>Quem e onde</span></GroupLabel>
      <Group>
        {(event.userId || event.userEmail) && (
          <Row $clickable={!!event.userId} $inset="62px" onClick={() => event.userId && onPush({ kind: "user", item: { id: event.userId, name: userMeta.name || event.userName || event.userEmail, ...userMeta } })}>
            <Avatar id={event.userId || event.userEmail} name={userMeta.name || event.userName || event.userEmail} color={userMeta.color} size={34} />
            <div className="main">
              <div className="title">{userMeta.name || event.userName || event.userEmail}</div>
              <div className="subtitle">{event.userEmail || userMeta.subtitle || "Usuário"}</div>
            </div>
            {event.userId && <div className="trailing"><FiChevronRight className="chevron" /></div>}
          </Row>
        )}
        {event.realUserId && (
          <Row $clickable $inset="62px" onClick={() => onPush({ kind: "user", item: { id: event.realUserId, name: lookup.users[event.realUserId]?.name || "God", ...lookup.users[event.realUserId] } })}>
            <Avatar id={event.realUserId} name={lookup.users[event.realUserId]?.name || "God"} color={lookup.users[event.realUserId]?.color} size={34} />
            <div className="main">
              <div className="title">{lookup.users[event.realUserId]?.name || event.realUserId}</div>
              <div className="subtitle" style={{ color: IOS.orange }}>Simulando o usuário acima</div>
            </div>
            <div className="trailing"><FiChevronRight className="chevron" /></div>
          </Row>
        )}
        {event.projectId && (
          <Row $clickable $inset="62px" onClick={() => onPush({ kind: "project", item: { id: event.projectId, name: projectMeta.name || event.projectName || event.projectId, ...projectMeta } })}>
            <Avatar id={event.projectId} name={projectMeta.name || event.projectName || "?"} color={projectMeta.color} size={34} square />
            <div className="main">
              <div className="title">{projectMeta.name || event.projectName || event.projectId}</div>
              <div className="subtitle">{projectMeta.subtitle || "Projeto"}</div>
            </div>
            <div className="trailing"><FiChevronRight className="chevron" /></div>
          </Row>
        )}
      </Group>

      <GroupLabel style={{ marginTop: 22 }}><span>Detalhes</span></GroupLabel>
      <Group>
        <InfoRow label="Módulo">{moduleMeta(event.module).label}</InfoRow>
        {fmtDuration(event.durationMs) && <InfoRow label="Duração">{fmtDuration(event.durationMs)}</InfoRow>}
        {event.entityType && <InfoRow label="Entidade" mono>{event.entityType} · {event.entityId || "—"}</InfoRow>}
        {event.operationId && <InfoRow label="Operação" mono>{event.operationId}</InfoRow>}
        {event.route && <InfoRow label="Rota" mono>{event.route}</InfoRow>}
        {event.ip && <InfoRow label="IP" mono>{event.ip}</InfoRow>}
        <InfoRow label="Origem">{{ "ravena-back": "Servidor", frontend: "Navegador", worker: "Worker" }[event.source] || event.source}</InfoRow>
        {event.userAgent && <InfoRow label="Navegador">{event.userAgent}</InfoRow>}
      </Group>

      {event.metadata && (
        <>
          <GroupLabel style={{ marginTop: 22 }}><span>Dados enviados</span></GroupLabel>
          <Group><CodeBlock>{JSON.stringify(event.metadata, null, 2)}</CodeBlock></Group>
        </>
      )}

      {opEvents.length > 0 && (
        <>
          <GroupLabel style={{ marginTop: 22 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><FiLink /> Mesma operação</span>
            <span>{opEvents.length}</span>
          </GroupLabel>
          <Group>
            {opEvents.map((op) => {
              const m = eventMeta(op);
              const OpIcon = op.status === "error" ? FiAlertCircle : m.icon;
              return (
                <Row key={op.id} $clickable $inset="58px" onClick={() => onPush({ kind: "event", item: op })}>
                  <IconTile $size={28} $color={op.status === "error" ? IOS.red : m.color}><OpIcon /></IconTile>
                  <div className="main">
                    <div className="title">{describe(op)}</div>
                    <div className="subtitle">{op.status === "error" ? op.errorMessage || "Erro" : fmtDuration(op.durationMs) || m.label}</div>
                  </div>
                  <div className="trailing"><span>{timeLabel(op.timestamp)}</span><FiChevronRight className="chevron" /></div>
                </Row>
              );
            })}
          </Group>
        </>
      )}
    </>
  );
}
